import {
    BasePagination,
    PaginationNavGroup,
    PaginationFirstButton,
    PaginationPrevButton,
    PaginationNextButton,
    PaginationLastButton,
    PaginationPageStatus,
    Card,
    CardHeader,
    Flex,
    Button,
    TextButton,
    Heading,
} from "@amzn/storm-ui";
import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { Paths } from "../../constants/paths";
import { workbenchStore } from "../../store/workbench";
import { getSearchParams } from "../../utils/url.utils";

import { PdfWorkbenchViewer } from "./PdfWorkbenchViewer";

const PdfContainer = styled.div`
    .react-pdf__Document {
        height: 500px;
    }
`;

const PdfNavigationBasePagination = styled(BasePagination)`
    width: 100%;
    justify-content: end;
`;

export const PdfWorkbenchCard: React.FunctionComponent = () => {
    const [getTotalSlides, removeSlide, getSlideAssetByIndex] = useStore(
        workbenchStore,
        (state) => [
            state.getTotalSlides,
            state.removeSlide,
            state.getSlideAssetByIndex,
        ],
    );

    const [selectedPageIndex, setselectedPageIndex] = React.useState<number>(0);
    const totalPages = getTotalSlides();

    const navigate = useNavigate();

    const isFirstPage = selectedPageIndex === 0;
    const hasNoPages = totalPages === 0;
    const lastPage = totalPages - 1;
    const setPreviousPage = () => setselectedPageIndex(selectedPageIndex - 1);
    const setNextPage = () => setselectedPageIndex(selectedPageIndex + 1);

    return (
        <Card data-testid="pdf-workbench-card">
            <CardHeader>
                <Heading renderAs="h3">Curate Slides</Heading>
            </CardHeader>
            {hasNoPages ? (
                <div data-testid="pdf-workench-card-empty">
                    Your workbench is currently empty.
                    <br />
                    Please return to the{" "}
                    <TextButton
                        data-testid="pdf-workbench-card-empty-home"
                        onClick={() =>
                            navigate(`${Paths.DISCOVERY}${getSearchParams()}`)
                        }
                    >
                        {" "}
                        <Heading renderAs="h4">home page</Heading>{" "}
                    </TextButton>{" "}
                    to add slides to your Workbench.
                </div>
            ) : (
                <>
                    <PdfContainer>
                        <PdfWorkbenchViewer
                            url={
                                getSlideAssetByIndex(selectedPageIndex)
                                    .previewUrl
                            }
                            pageIndex={
                                getSlideAssetByIndex(selectedPageIndex).slide
                            }
                        />
                    </PdfContainer>
                    <Flex alignItems="center">
                        <Button
                            data-testid="pdf-workbench-card-remove-slide"
                            onClick={() => {
                                removeSlide(
                                    getSlideAssetByIndex(selectedPageIndex)
                                        .identifier,
                                    getSlideAssetByIndex(selectedPageIndex)
                                        .slide,
                                );
                                if (selectedPageIndex > 0) {
                                    setPreviousPage();
                                }
                            }}
                        >
                            Remove from workbench
                        </Button>
                        <PdfNavigationBasePagination>
                            <PaginationPageStatus
                                currentPageLabel={`Slide ${selectedPageIndex + 1} of ${totalPages}`}
                            />
                            <PaginationNavGroup>
                                <PaginationFirstButton
                                    id="pdf-workbench-card-first"
                                    ariaLabel="Navigate to first page"
                                    onClick={() => setselectedPageIndex(0)}
                                    disabled={isFirstPage || hasNoPages}
                                />
                                <PaginationPrevButton
                                    id="pdf-workbench-card-prev"
                                    ariaLabel="Navigate to previous page"
                                    onClick={() => setPreviousPage()}
                                    disabled={isFirstPage || hasNoPages}
                                />
                                <PaginationNextButton
                                    id="pdf-workbench-card-next"
                                    ariaLabel="Navigate to next page"
                                    onClick={() => setNextPage()}
                                    disabled={
                                        selectedPageIndex === lastPage ||
                                        hasNoPages
                                    }
                                />
                                <PaginationLastButton
                                    id="pdf-workbench-card-last"
                                    ariaLabel="Navigate to last page"
                                    onClick={() =>
                                        setselectedPageIndex(lastPage)
                                    }
                                    disabled={
                                        selectedPageIndex === lastPage ||
                                        hasNoPages
                                    }
                                />
                            </PaginationNavGroup>
                        </PdfNavigationBasePagination>
                    </Flex>
                </>
            )}
        </Card>
    );
};
